import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useProjectContext } from "../layout/ProjectContext";
import {
  useGetDistortionPairsQuery,
  useCreateDistortionPairMutation,
  useDeleteDistortionPairMutation,
} from "../features/distortion-pairs/apiSlice";
import { useGetDevicesByProjectQuery } from "../features/devices/apiSlice";
import { useGetAlertProfilesQuery } from "../features/alert-profiles/apiSlice";
import EmptyHint from "../components/EmptyHint";

const PAIRABLE_DEVICE_TYPES = new Set([
  "movement",
  "ground_movement",
  "building_movement",
  "utility_movement",
]);

const ANGULAR_PROFILE_TYPES = [
  "angular_distortion_building",
  "angular_distortion_utility",
] as const;

type AngularProfileType = (typeof ANGULAR_PROFILE_TYPES)[number];

function determineAngularProfileType(
  pointAType?: string,
  pointBType?: string,
): AngularProfileType | null {
  if (!pointAType || !pointBType) return null;
  const isBuilding =
    pointAType === "building_movement" && pointBType === "building_movement";

  return isBuilding
    ? "angular_distortion_building"
    : "angular_distortion_utility";
}

const ANGULAR_PROFILE_LABELS: Record<AngularProfileType, string> = {
  angular_distortion_building: "Angular Distortion (Building)",
  angular_distortion_utility: "Angular Distortion (Utility)",
};

const DEFAULT_ANGULAR_PROFILE_NAMES: Record<AngularProfileType, string> = {
  angular_distortion_building: "Default Angular Distortion (Building)",
  angular_distortion_utility: "Default Angular Distortion (Utility)",
};

const DistortionPairs: React.FC = () => {
  const { projectId } = useProjectContext();

  const { data: pairs, isFetching } = useGetDistortionPairsQuery(
    projectId as number,
    { skip: projectId == null },
  );
  const { data: devices } = useGetDevicesByProjectQuery(projectId as number, {
    skip: projectId == null,
  });
  const { data: alertProfiles } = useGetAlertProfilesQuery(
    projectId as number,
    { skip: projectId == null },
  );
  const [createPair] = useCreateDistortionPairMutation();
  const [deletePair] = useDeleteDistortionPairMutation();

  const [open, setOpen] = useState(false);
  const [checkpointA, setCheckpointA] = useState("");
  const [checkpointB, setCheckpointB] = useState("");
  const [label, setLabel] = useState("");

  const pairableDevices = useMemo(
    () =>
      (devices ?? []).filter((d) => PAIRABLE_DEVICE_TYPES.has(d.device_type)),
    [devices],
  );

  const checkpointADevice = useMemo(
    () => pairableDevices.find((d) => d.id === checkpointA),
    [pairableDevices, checkpointA],
  );

  const checkpointBDevice = useMemo(
    () => pairableDevices.find((d) => d.id === checkpointB),
    [pairableDevices, checkpointB],
  );

  const profileType = determineAngularProfileType(
    checkpointADevice?.device_type,
    checkpointBDevice?.device_type,
  );

  const resetForm = () => {
    setCheckpointA("");
    setCheckpointB("");
    setLabel("");
  };

  const submit = async () => {
    if (projectId == null || !profileType) return;
    if (checkpointA === checkpointB) {
      Swal.fire({
        icon: "warning",
        title: "Pick two different devices",
      });
      return;
    }

    const matchingProfiles = (alertProfiles ?? []).filter(
      (p) => p.profile_type === profileType,
    );
    if (matchingProfiles.length === 0) {
      Swal.fire({
        icon: "error",
        title: "No action profile found",
        text: `Please create an "${ANGULAR_PROFILE_LABELS[profileType]}" action profile before adding pairs of this type.`,
      });
      return;
    }
    const chosenProfile =
      matchingProfiles.find(
        (p) => p.description === DEFAULT_ANGULAR_PROFILE_NAMES[profileType],
      ) ?? matchingProfiles[0];

    try {
      await createPair({
        projectId,
        pointADeviceId: checkpointA,
        pointBDeviceId: checkpointB,
        pairLabel: label || undefined,
        alertProfileId: chosenProfile.id,
      }).unwrap();
      setOpen(false);
      resetForm();
    } catch {
      Swal.fire({
        icon: "error",
        title: "Create failed",
        text: "Both devices must be movement checkpoints with valid coordinates.",
      });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete pair?",
      text: name,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!res.isConfirmed) return;
    try {
      await deletePair(id).unwrap();
    } catch {
      Swal.fire({ icon: "error", title: "Delete failed" });
    }
  };

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Choose a project from the top bar to manage distortion pairs."
      />
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Distortion Pairs</h1>
          <div className="sub">
            {(pairs ?? []).length} pair{(pairs ?? []).length === 1 ? "" : "s"} ·
            angular distortion between two movement checkpoints
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
        >
          <i className="bi bi-plus-lg" /> New pair
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Pairs</h2>
        </div>
        {isFetching ? (
          <EmptyHint
            icon="bi-hourglass-split"
            title="Loading…"
            subtitle="Fetching distortion pairs."
          />
        ) : (pairs ?? []).length === 0 ? (
          <EmptyHint
            icon="bi-rulers"
            title="No distortion pairs"
            subtitle="Create a pair of settlement devices to track angular distortion."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Pair</th>
                <th>Checkpoint A</th>
                <th>Checkpoint B</th>
                <th>Angular Distortion Type</th>
                <th>Baseline</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {(pairs ?? []).map((p) => {
                const rowProfileType = determineAngularProfileType(
                  p.pointAType,
                  p.pointBType,
                );
                return (
                  <tr key={p.id} style={{ opacity: p.isActive ? 1 : 0.5 }}>
                    <td className="fw-600">
                      {p.pairLabel || `${p.pointAName} ↔ ${p.pointBName}`}
                    </td>

                    <td>{p.pointAName ?? p.pointADeviceId}</td>
                    <td>{p.pointBName ?? p.pointBDeviceId}</td>
                    <td>
                      {rowProfileType === "angular_distortion_utility" && (
                        <b>Utility</b>
                      )}
                      {rowProfileType === "angular_distortion_building" && (
                        <b>Building</b>
                      )}
                    </td>
                    <td>{p.baselineDistanceM?.toFixed(2)} m</td>
                    <td>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() =>
                          handleDelete(
                            p.id,
                            p.pairLabel || `${p.pointAName} ↔ ${p.pointBName}`,
                          )
                        }
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      <div className={`modal-backdrop ${open ? "show" : ""}`}>
        {open && (
          <div className="modal">
            <div className="modal-head">
              <h3>New distortion pair</h3>
              <button
                className="btn btn-ghost btn-icon"
                onClick={() => setOpen(false)}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="modal-body">
              {pairableDevices.length < 2 && (
                <div className="badge warn mb-3">
                  Project needs at least two movement checkpoints.
                </div>
              )}
              <div className="field mb-3">
                <label>Label (optional)</label>
                <input
                  className="input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Building A frontage"
                />
              </div>
              <div className="field mb-3">
                <label>Checkpoint A</label>
                <select
                  className="select"
                  value={checkpointA}
                  onChange={(e) => setCheckpointA(e.target.value)}
                >
                  <option value="">Select device…</option>
                  {pairableDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.device_name} ({d.device_type})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Checkpoint B</label>
                <select
                  className="select"
                  value={checkpointB}
                  onChange={(e) => setCheckpointB(e.target.value)}
                >
                  <option value="">Select device…</option>
                  {pairableDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.device_name} ({d.device_type})
                    </option>
                  ))}
                </select>
              </div>
              <div className="field mt-3">
                <label>Angular distortion profile type</label>
                {profileType === "angular_distortion_utility" && <b>Utility</b>}
                {profileType === "angular_distortion_building" && (
                  <b>Building</b>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submit}
                disabled={!checkpointA || !checkpointB}
              >
                Create pair
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DistortionPairs;
